import React, { useEffect, useRef, useMemo } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { CLASSIFICATIONS, DISEASES } from '../mock';

const DARK_STYLE = {
  version: 8,
  sources: {
    carto: {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
        'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
        'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
        'https://d.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
    },
  },
  layers: [
    { id: 'bg', type: 'background', paint: { 'background-color': '#040c0e' } },
    {
      id: 'carto-dark',
      type: 'raster',
      source: 'carto',
      minzoom: 0,
      maxzoom: 19,
      paint: { 'raster-opacity': 0.9 },
    },
  ],
};

const MapView = ({ signals, activeDiseases, classFilter, onSelectSignal }) => {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markersRef = useRef([]);

  const diseaseMap = useMemo(() => Object.fromEntries(DISEASES.map(d => [d.id, d])), []);

  const groupedSignals = useMemo(() => {
    const filtered = signals.filter(s =>
      activeDiseases.includes(s.diseaseId) && classFilter[s.classification]
    );
    const groups = new Map();

    filtered.forEach((signal) => {
      const key = signal.country || `${signal.lng},${signal.lat}`;
      const current = groups.get(key) || {
        country: signal.country || signal.place,
        lng: signal.lng,
        lat: signal.lat,
        signals: [],
      };
      current.signals.push(signal);
      groups.set(key, current);
    });

    const rank = { official: 3, emerging: 2, chatter: 1 };
    return [...groups.values()].map((group) => {
      const sorted = [...group.signals].sort((a, b) => (a.hoursAgo || 0) - (b.hoursAgo || 0));
      const primary = sorted.reduce((best, signal) =>
        rank[signal.classification] > rank[best.classification] ? signal : best, sorted[0]);
      return { ...group, signals: sorted, primary };
    });
  }, [signals, activeDiseases, classFilter]);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: DARK_STYLE,
      center: [10, 25],
      zoom: 1.8,
      minZoom: 1.3,
      maxZoom: 7.5,
      attributionControl: { compact: true },
      dragRotate: false,
      pitchWithRotate: false,
      renderWorldCopies: true,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false, visualizePitch: false }), 'bottom-right');
    map.touchZoomRotate.disableRotation();
    mapRef.current = map;

    const resizeMap = () => map.resize();
    requestAnimationFrame(resizeMap);
    setTimeout(resizeMap, 100);
    setTimeout(resizeMap, 400);
    setTimeout(resizeMap, 1000);

    const ro = new ResizeObserver(resizeMap);
    ro.observe(containerRef.current);
    window.addEventListener('resize', resizeMap);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', resizeMap);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    groupedSignals.forEach(group => {
      const s = group.primary;
      const el = document.createElement('div');
      el.className = `signal-cluster ${s.classification}`;
      const color = CLASSIFICATIONS[s.classification].color;
      const count = group.signals.length;
      const size = Math.max(22, Math.min(46, 20 + Math.sqrt(count) * 7));
      el.style.setProperty('--pin-color', color);
      el.style.setProperty('--pin-size', `${size}px`);
      el.innerHTML = count > 1
        ? `<span class="signal-cluster__count">${count}</span>`
        : '';

      const disease = diseaseMap[s.diseaseId];

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onSelectSignal && onSelectSignal(s);
      });

      const rows = group.signals.slice(0, 4).map(signal => {
        const signalDisease = diseaseMap[signal.diseaseId];
        const signalClass = CLASSIFICATIONS[signal.classification];
        return `
          <div style="border-top:1px solid rgba(26,61,68,.7);padding-top:7px;margin-top:7px">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:3px">
              <span style="width:7px;height:7px;border-radius:50%;background:${signalClass.color};display:inline-block;box-shadow:0 0 8px ${signalClass.color}"></span>
              <span style="font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:#7fb4be">${escapeHtml(signalClass.label)}</span>
              <span style="font-size:10px;color:${signalDisease?.color || '#9ad'}">${escapeHtml(signalDisease?.name || '')}</span>
            </div>
            <div style="font-size:12px;font-weight:600;color:#e6f7fa;line-height:1.35">${escapeHtml(signal.headline)}</div>
            <div style="display:flex;justify-content:space-between;font-size:10.5px;color:#6b8a92;margin-top:4px">
              <span>${escapeHtml(signal.source)}</span><span>${signal.hoursAgo || 0}h ago</span>
            </div>
          </div>
        `;
      }).join('');

      const popup = new maplibregl.Popup({ offset: 14, closeButton: true, maxWidth: '280px' })
        .setHTML(`
          <div style="font-family:Inter,sans-serif">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px">
              <span style="width:8px;height:8px;border-radius:50%;background:${color};display:inline-block"></span>
              <span style="font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:#7fb4be">${escapeHtml(group.country)}</span>
              <span style="font-size:10px;color:#4a6b72">&middot;</span>
              <span style="font-size:10px;color:${disease?.color || '#9ad'}">${count} signal${count === 1 ? '' : 's'}</span>
            </div>
            ${rows}
          </div>
        `);

      const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([group.lng, group.lat])
        .setPopup(popup)
        .addTo(map);

      markersRef.current.push(marker);
    });
  }, [groupedSignals, diseaseMap, onSelectSignal]);

  return (
    <div ref={containerRef} className="absolute inset-0" style={{ background: '#040c0e', width: '100%', height: '100%' }} />
  );
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

export default MapView;
